import React, { useEffect, useRef, useState } from "react";
import { FiLock, FiChevronDown, FiChevronUp, FiX } from "react-icons/fi";

/**
 * Tracker picker for a subscription plan.
 *
 * Default trackers are on every plan, so they show greyed out and checked in
 * the list, and as locked chips. Only the optional trackers can be toggled, and
 * only their ids go through `selectedIds` / `onChange`.
 *
 * @param {Object} props
 * @param {{id: string, title: string, is_default: boolean}[]} props.trackers - From get-common-utils.
 * @param {string[]} props.selectedIds - Selected optional tracker ids.
 * @param {(ids: string[]) => void} props.onChange - Receives the complete new list.
 * @param {boolean} [props.disabled]
 */
const TrackerMultiSelect = ({ trackers, selectedIds, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEscape = (e) => {
      // Close only the dropdown, not the modal around it.
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    const node = ref.current;
    document.addEventListener("mousedown", onOutside);
    node?.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      node?.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const defaults = trackers.filter((t) => t.is_default);
  const selectedAdditional = trackers.filter(
    (t) => !t.is_default && selectedIds.includes(t.id)
  );
  const query = search.trim().toLowerCase();
  const visible = query
    ? trackers.filter((t) => (t.title || "").toLowerCase().includes(query))
    : trackers;

  const toggle = (id) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div ref={ref} className="relative">
      <div className="flex items-baseline justify-between mb-1">
        <label htmlFor="tracker-select-input" className="block font-semibold text-gray-700">
          Trackers<span className="text-red-500">*</span>
        </label>
        <span className="text-xs text-gray-500">
          {defaults.length + selectedAdditional.length} selected
        </span>
      </div>

      <div
        className={`flex items-center w-full border rounded-md ${
          open ? "ring-2 ring-orange-primary border-orange-primary" : "focus-within:ring-2 focus-within:ring-orange-primary"
        } ${disabled ? "opacity-50" : ""}`}
      >
        <input
          id="tracker-select-input"
          type="text"
          autoComplete="off"
          placeholder="Select trackers"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            // Enter must not submit the plan form from here.
            if (e.key === "Enter") e.preventDefault();
          }}
          disabled={disabled}
          role="combobox"
          aria-controls="tracker-select-listbox"
          aria-haspopup="listbox"
          aria-expanded={open}
          className="flex-1 p-2 rounded-md bg-transparent focus:!outline-none focus-visible:!outline-none"
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close tracker list" : "Open tracker list"}
          className="px-2 text-gray-500 focus:!outline-none focus-visible:!outline-none"
        >
          {open ? <FiChevronUp /> : <FiChevronDown />}
        </button>
      </div>

      {open && (
        <div
          id="tracker-select-listbox"
          role="listbox"
          aria-multiselectable="true"
          className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {visible.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-400">
              {trackers.length === 0 ? "No trackers available" : "No matching trackers"}
            </p>
          )}
          {visible.map((t) =>
            t.is_default ? (
              <label
                key={t.id}
                title="Included in all plans"
                className="flex items-center gap-3 px-3 py-2 text-gray-400 cursor-not-allowed"
              >
                <input type="checkbox" checked disabled className="accent-orange-primary opacity-50 focus:!outline-none focus-visible:!outline-none" />
                <span className="flex-1">{t.title}</span>
                <FiLock size={12} />
              </label>
            ) : (
              <label
                key={t.id}
                className="flex items-center gap-3 px-3 py-2 text-gray-800 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(t.id)}
                  onChange={() => toggle(t.id)}
                  disabled={disabled}
                  className="accent-orange-primary focus:!outline-none focus-visible:!outline-none"
                />
                <span className="flex-1">{t.title}</span>
              </label>
            )
          )}
        </div>
      )}

      {(defaults.length > 0 || selectedAdditional.length > 0) && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {defaults.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-sm"
            >
              <FiLock size={11} />
              {t.title}
            </span>
          ))}
          {selectedAdditional.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-light-orange text-orange-primary text-sm font-medium"
            >
              {t.title}
              <button
                type="button"
                aria-label={`Remove ${t.title}`}
                disabled={disabled}
                onClick={() => toggle(t.id)}
                className="hover:text-orange-600"
              >
                <FiX size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrackerMultiSelect;
